'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { useAuthUser, useRedirectToLogin, useAuthInitializing } from '@/hooks';
import { useAppContext } from '@/context';
import { Spinner } from '@/components/spinner';
import {
  updateTargetInStorage,
  updateLanguageInStorage,
  AuthStorage,
  updateAuthInStorage
} from '@/utils/auth';
import { getExternalServiceUrl } from '@/utils/environment';
import { extractLanguageInfo, synchronizeLanguage, getCurrentLanguage } from '@/utils/languages';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/config';
import { checkToken } from '@/utils/checkToken';
import { fixUrlForCloudFront } from '@/utils/cloudfront';
import { normalizeUrl, safeNavigate } from '@/utils/navigation';
import {
  Campaigns,
  CampaignPageCollaborations,
  CampaignPageDrafts,
  CampaignPageSummary,
  CampaignPageSentiment,
  CampaignPageBeat,
  CampaignPageList
} from '@/views/campaigns';
import { CampaignPageCuration } from '@/views/campaigns/CampaignPageCuration';
import { Settings } from '@/views/settings/Settings';
import { Team } from '@/views/team/Team';
import { ContentHub } from '@/views/content-hub/ContentHub';
import { Payouts } from '@/views/payouts/Payouts';
import { Sentiment } from '@/views/sentiment/Sentiment';
import { Ambassadors } from '@/views/ambassadors';
import { CampaignPlans } from '@/views/plans';
import { Collaborations } from '@/views/collabs/Collaborations';
import { Collaboration } from '@/views/collaborations/Collaboration';
import { InviteView } from '@/views/invite/InviteView';
import { Influencer } from '@/views/influencer/Influencer';
import { Login } from '@/views/login';
import { Signup } from '@/views/signup';
import { VerifyToken } from '@/views/verify-token/VerifyToken';

// Reusable spinner component to reduce duplication
function LoadingSpinner() {
  return (
    <div
      style={{
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div style={{ width: '50px', height: '50px' }}>
        <Spinner />
      </div>
    </div>
  );
}

function Home() {
  const router = useRouter();
  const user = useAuthUser();
  const redirectToLogin = useRedirectToLogin();
  const isAuthInitializing = useAuthInitializing();
  const { t } = useTranslation();
  const { setCampaignId } = useAppContext();
  const [isClient, setIsClient] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isProcessingJwt, setIsProcessingJwt] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [languageInfo, setLanguageInfo] = useState<{
    languageCode: string | null;
    languagePrefix: string;
    routeWithoutLanguage: string;
  }>({
    languageCode: null,
    languagePrefix: '',
    routeWithoutLanguage: ''
  });

  // Helper function to redirect collaboration sub-routes while preserving query parameters
  const redirectCollaborationSubRoute = (collaborationId: string, tab: string) => {
    if (isNavigating) {
      return null;
    }

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('tab', tab);
    const queryString = urlParams.toString();
    const redirectUrl = `${languageInfo.languagePrefix}/connect/collaborations/${collaborationId}?${queryString}`;

    // Use router.push instead of window.location.replace to avoid hard navigation conflicts
    if (router.isReady) {
      setIsNavigating(true);
      router.push(redirectUrl).finally(() => {
        setIsNavigating(false);
      });
    } else {
      // Fallback to window.location.replace only if router is not ready
      window.location.replace(redirectUrl);
    }
    return null;
  };

  // Helper function to check if current path is an auth route
  const isOnAuthRoute = () => {
    return currentPath.match(/^(?:\/[a-z]{2})?\/(login|signup|auth\/[^/?]+)\/?(\?.*)?$/);
  };

  // Helper function to redirect to appropriate dashboard based on user type
  const redirectToDashboard = () => {
    if (user.is_client) {
      setPendingNavigation(`${languageInfo.languagePrefix}/connect/campaigns`);
    } else {
      setPendingNavigation(`${languageInfo.languagePrefix}/connect/collaborations`);
    }
  };

  // Helper function to parse route components
  const parseRoute = (path: string) => {
    // Remove query parameters before parsing the path
    const pathWithoutQuery = path.split('?')[0];
    const pathParts = pathWithoutQuery.split('/').filter(Boolean);
    const isLanguagePrefixed = pathParts.length > 0 && /^[a-z]{2}$/.test(pathParts[0]);
    const routeParts = isLanguagePrefixed ? pathParts.slice(1) : pathParts;
    const connectIndex = routeParts.indexOf('connect');

    return { pathParts, isLanguagePrefixed, routeParts, connectIndex };
  };

  // Check if a given path should trigger a redirect to login (when no auth) or dashboard (when authenticated)
  const shouldRedirectToLoginOrDashboard = (path: string): boolean => {
    // Root path (with or without language prefix)
    if (path.match(/^(?:\/[a-z]{2})?\/?$/)) {
      return true;
    }

    // Connect route (with or without language prefix)
    if (path.match(/^(?:\/[a-z]{2})?\/connect\/?$/)) {
      return true;
    }

    return false;
  };

  // Initialize load-time logic - store URL and JWT token
  const initializeLoadTimeLogic = () => {
    if (typeof window === 'undefined') return;

    const currentUrl = window.location.href;
    const url = new URL(currentUrl);

    // Store the loading URL with both URL and query parameters
    const updatedAuth: Partial<AuthStorage> = {
      start_url: currentUrl
    };

    // Check for JWT token in query parameters
    const jwtParam = url.searchParams.get('jwt');
    if (jwtParam) {
      updatedAuth.token = jwtParam;
    }

    // Store updated auth data
    updateAuthInStorage(updatedAuth);
  };

  // Process JWT token if present
  const processJwtToken = async () => {
    if (typeof window === 'undefined') return;

    const { jwt } = router?.query || {};

    // Also check URL directly for JWT parameter (in case router.query doesn't have it)
    let jwtFromUrl = null;
    const currentUrl = new URL(window.location.href);
    jwtFromUrl = currentUrl.searchParams.get('jwt');

    const hasJwt = jwt || jwtFromUrl;

    if (hasJwt && router.pathname !== '/influencer/[id]') {
      setIsProcessingJwt(true);

      // Remove JWT parameter and reconstruct URL
      const params = currentUrl.searchParams;
      params.delete('jwt');
      const queryString = params.toString() ? `?${params.toString()}` : '';

      // Get the pathname without trailing slash for consistent processing
      let { pathname } = currentUrl;
      if (pathname.endsWith('/') && pathname !== '/') {
        pathname = pathname.slice(0, -1);
      }

      let cleanedUrl = pathname + queryString;

      // Handle old collaboration paths to new tab-based URLs
      // This must be done BEFORE any other URL modifications
      const oldToNewPaths: Record<string, string> = {
        '/drafts': '?tab=DRAFTS',
        '/publications': '?tab=PUBLICATIONS',
        '/statistics': '?tab=SCREENSHOTS1'
      };

      // Apply the transformation only if the URL includes `/connect/collaborations`
      if (cleanedUrl.includes('/connect/collaborations')) {
        Object.keys(oldToNewPaths).forEach((oldPath) => {
          // Check if the URL contains the old path (not just ends with it)
          if (cleanedUrl.includes(oldPath)) {
            // Extract the base URL and query parameters
            const urlParts = cleanedUrl.split('?');
            const baseUrl = urlParts[0];
            const existingQuery = urlParts[1] || '';

            // Remove the old path from the base URL
            const newBaseUrl = baseUrl.replace(oldPath, '');

            // Add the tab parameter
            const tabParam = oldToNewPaths[oldPath].replace('?', '');
            const newQuery = existingQuery ? `${existingQuery}&${tabParam}` : tabParam;

            cleanedUrl = `${newBaseUrl}?${newQuery}`;
          }
        });
      }

      // For campaign URLs, ensure consistent formatting
      // The main routing logic expects campaign URLs without trailing slashes
      // unless they have sub-routes or query parameters
      if (cleanedUrl.includes('/connect/campaigns/')) {
        // Remove trailing slash if it exists and there are no query parameters
        if (cleanedUrl.endsWith('/') && !cleanedUrl.includes('?')) {
          cleanedUrl = cleanedUrl.slice(0, -1);
        }
      }

      const link = cleanedUrl || '/connect';

      try {
        await checkToken(router, link, hasJwt);
        // checkToken will handle the redirect via window.location.href
      } catch (error) {
        console.error('JWT processing failed:', error);
        setIsProcessingJwt(false);
      }
    }
  };

  // Helper function to handle errors with logging and redirect
  const handleError = (errorMessage: string, errorDetails?: Record<string, unknown>) => {
    console.error('Routing error:', errorMessage, errorDetails);

    // Schedule navigation instead of calling router directly
    redirectToDashboard();

    return null; // Return null to prevent rendering
  };

  useEffect(() => {
    setIsClient(true);

    // Initialize load-time logic first
    initializeLoadTimeLogic();

    // Get the current pathname to determine which component to render
    // Use router.asPath if available, otherwise fall back to window.location
    const pathname =
      router.asPath || (typeof window !== 'undefined' ? window.location.pathname : '/');
    setCurrentPath(pathname);

    // Extract language information once
    const langInfo = extractLanguageInfo(pathname);
    setLanguageInfo(langInfo);

    // Apply language to i18n if present
    if (langInfo.languageCode) {
      localStorage.setItem('lang', langInfo.languageCode);
      updateLanguageInStorage(langInfo.languageCode);
      if (i18n.language !== langInfo.languageCode) {
        i18n.changeLanguage(langInfo.languageCode);
      }
    }
    // Note: Language detection from user preferences is now handled after JWT processing
    // to avoid race conditions when switching users
  }, [router.asPath]);

  // Process JWT token when router is ready
  useEffect(() => {
    if (router.isReady) {
      processJwtToken();
    }
  }, [router.isReady]);

  // Handle language detection after JWT processing to avoid race conditions
  useEffect(() => {
    if (!isClient || !router.isReady || isProcessingJwt) return;

    // Only apply user language preference if no URL language prefix was detected
    if (!languageInfo.languageCode) {
      const currentLanguage = getCurrentLanguage();
      if (currentLanguage && i18n.language !== currentLanguage) {
        synchronizeLanguage(currentLanguage, 'index-routing');
      }
    }
  }, [isClient, router.isReady, isProcessingJwt, languageInfo.languageCode]);

  // Handle pending navigation
  useEffect(() => {
    if (pendingNavigation && router.isReady && !isNavigating) {
      // Check if we're already on the target URL to avoid hard navigation error
      const currentUrl = router.asPath;
      const targetUrl = pendingNavigation;

      // Normalize URLs by removing trailing slashes and query parameters for comparison
      const normalizedCurrentUrl = normalizeUrl(currentUrl);
      const normalizedTargetUrl = normalizeUrl(targetUrl);

      // If we're already on the target URL, skip the navigation to prevent Next.js invariant error
      if (normalizedCurrentUrl === normalizedTargetUrl) {
        setPendingNavigation(null);
        return;
      }

      setIsNavigating(true);
      router.push(pendingNavigation).finally(() => {
        setIsNavigating(false);
        setPendingNavigation(null);
      });
    }
  }, [pendingNavigation, router, isNavigating]);

  // Update currentPath when router changes
  useEffect(() => {
    if (router.isReady) {
      setCurrentPath(router.asPath);
    } else {
      // Update currentPath even when router isn't ready to avoid stale state
      setCurrentPath(window.location.pathname);
    }
  }, [router.asPath, router.isReady]);

  // Helper functions for authentication checks
  const getAuthToken = () => {
    if (typeof window === 'undefined') return null;
    try {
      const authData = localStorage.getItem('auth');
      return authData ? JSON.parse(authData).token : null;
    } catch (error) {
      console.warn('Failed to parse auth data from localStorage:', error);
      return null;
    }
  };

  const isUserAuthenticated = () => {
    // Don't consider user authenticated if auth is still initializing
    if (isAuthInitializing) {
      return false;
    }
    return !!(user?.user_id && getAuthToken());
  };

  const shouldRedirectToLogin = () => {
    // Don't redirect to login if auth is still initializing
    if (isAuthInitializing) {
      return false;
    }
    return !user || !user.user_id || !getAuthToken();
  };

  const getDashboardRoute = () => {
    if (user.is_client) {
      return `${languageInfo.languagePrefix}/connect/campaigns`;
    }
    return `${languageInfo.languagePrefix}/connect/collaborations`;
  };

  // Manage campaignId based on current route (as a side effect, not during render)
  useEffect(() => {
    if (!isClient || !router.isReady) return;

    const { routeParts, connectIndex } = parseRoute(currentPath);

    // Check if we're on a campaign detail page
    if (connectIndex !== -1 && connectIndex + 1 < routeParts.length) {
      const routeType = routeParts[connectIndex + 1];

      if (routeType === 'campaigns') {
        const campaignsIndex = connectIndex + 1;
        if (campaignsIndex + 1 < routeParts.length) {
          // We have a campaign ID
          const campaignId = routeParts[campaignsIndex + 1];
          setCampaignId(campaignId);
          return;
        }
      }
    }

    // For all other routes, clear the campaignId
    setCampaignId(null);
  }, [isClient, router.isReady, currentPath, setCampaignId]);

  // Handle route-based redirects
  useEffect(() => {
    if (!isClient || !router.isReady || pendingNavigation || isProcessingJwt) return;

    // Handle /goto/<name> routes FIRST - before general auth checks
    const { routeParts } = parseRoute(currentPath);

    if (routeParts.length === 2 && routeParts[0] === 'goto') {
      const gotoName = routeParts[1];

      // Only process 'planner' goto name, redirect all others to login
      if (gotoName !== 'planner') {
        redirectToLogin();
        return;
      }

      // Get query parameters from current URL
      const urlParams = new URLSearchParams(window.location.search);

      // Check if JWT token exists in localStorage and append it
      const jwtToken = getAuthToken();
      if (jwtToken) {
        urlParams.set('jwt', jwtToken);
      }

      // Only add query string if there are actually meaningful parameters
      // URLSearchParams.toString() can return "=" for empty parameters, so we need to check for actual content
      const queryString = urlParams.toString();
      const hasMeaningfulParams = queryString && queryString !== '=' && queryString.length > 0;

      // Determine target URL based on environment
      let targetUrl = getExternalServiceUrl(gotoName);

      // Add query parameters if they exist and are meaningful
      if (hasMeaningfulParams) {
        targetUrl += `?${queryString}`;
      }

      // Redirect to the target URL
      window.location.href = targetUrl;
      return;
    }

    // Handle /auth/<name> routes - after goto routes but before general auth checks
    if (routeParts.length === 2 && routeParts[0] === 'auth') {
      const authName = routeParts[1];

      // Only process 'summary' auth name, redirect all others to login
      if (authName !== 'summary') {
        redirectToLogin();
        return;
      }

      // Check if JWT token exists in localStorage
      const jwtToken = getAuthToken();

      if (jwtToken) {
        // Case A: JWT token exists - redirect immediately to external service
        const targetUrl = `${getExternalServiceUrl(authName)}?jwt=${jwtToken}`;
        window.location.href = targetUrl;
        return;
      }

      // Case B: No JWT token - redirect to login with target parameter
      updateTargetInStorage(authName);
      redirectToLogin();
      return;
    }

    // Then check if user should be redirected to login (no authentication at all)
    if (shouldRedirectToLogin()) {
      // Only redirect to login if we're not already on login/signup routes
      if (!isOnAuthRoute()) {
        redirectToLogin();
        return;
      }
    }

    // Handle root route (/) and /connect route specifically - these should redirect based on user type
    // Note: This is now handled directly in the render method for immediate redirects
    if (shouldRedirectToLoginOrDashboard(currentPath)) {
      if (isUserAuthenticated()) {
        setPendingNavigation(getDashboardRoute());
      }
      // For unauthenticated users, redirect is handled in render method
      return;
    }

    // Handle user redirects for authenticated users who aren't on valid routes
    if (isUserAuthenticated()) {
      // Check if we're already on a valid connect route (with or without language prefix)
      const isOnValidConnectRoute = currentPath.match(
        /^(?:\/[a-z]{2})?\/connect\/(campaigns|collaborations|settings|team|content-hub|plans|payouts|sentiment|ambassadors)/
      );
      const isOnSpecificCampaignPage = currentPath.match(
        /^(?:\/[a-z]{2})?\/connect\/campaigns\/[^/]+/
      );
      const isOnSpecificCollaborationPage = currentPath.match(
        /^(?:\/[a-z]{2})?\/connect\/collaborations\/[^/]+/
      );
      const isOnCollaborationInvitePage = currentPath.match(
        /^(?:\/[a-z]{2})?\/connect\/collaborations\/[^/]+\/invite/
      );
      const isOnInfluencerPage = currentPath.match(/^(?:\/[a-z]{2})?\/influencer\/[^/]+/);
      const isOnCampaignSubRoute = currentPath.match(
        /^(?:\/[a-z]{2})?\/connect\/campaigns\/[^/]+\/(curation|collaborations|drafts|summary|sentiment|beat)/
      );

      // Don't redirect if user is on login, signup, or other auth-related pages
      const isOnAuthPage = currentPath.match(
        /^(?:\/[a-z]{2})?\/?(login|signup|logout|verify-email|verify-token)/
      );

      // Handle root path redirects for authenticated users
      const isOnRootPath = currentPath === '/' || currentPath.match(/^\/[a-z]{2}\/?$/);

      if (isOnRootPath) {
        // Redirect authenticated users from root to appropriate connect route
        redirectToDashboard();
        return;
      }

      if (
        !isOnValidConnectRoute &&
        !isOnSpecificCampaignPage &&
        !isOnSpecificCollaborationPage &&
        !isOnCollaborationInvitePage &&
        !isOnInfluencerPage &&
        !isOnCampaignSubRoute &&
        !isOnAuthPage
      ) {
        redirectToDashboard();
      }
    }
  }, [
    isClient,
    router.isReady,
    currentPath,
    user,
    languageInfo.languagePrefix,
    isProcessingJwt,
    isAuthInitializing
  ]);

  // Wait for client-side hydration, JWT processing, and auth initialization
  // But allow login/signup routes to render even if router isn't ready
  if (!isClient || isProcessingJwt || isAuthInitializing) {
    return <LoadingSpinner />;
  }

  // If router isn't ready, only show spinner for non-auth routes
  if (!router.isReady) {
    if (!isOnAuthRoute()) {
      return <LoadingSpinner />;
    }
    // If user is on auth route, continue to render the auth component
  }

  // If user should be redirected to login and we're not already on login/signup, show spinner briefly
  if (shouldRedirectToLogin()) {
    if (!isOnAuthRoute()) {
      return <LoadingSpinner />;
    }
    // If user is already on auth route, continue to render the auth component
  }

  // Redirect authenticated influencers to collab app (all environments)
  if (isUserAuthenticated() && user?.is_influencer) {
    const jwtToken = getAuthToken();
    if (jwtToken) {
      const collabUrl = `${getExternalServiceUrl('collab')}/connect/?jwt=${jwtToken}`;
      window.location.href = collabUrl;
      return <LoadingSpinner />;
    }
  }

  // Handle root route (/) and /connect route specifically - these should redirect based on user type
  // This includes both /, /connect and /<lang>/connect routes
  if (shouldRedirectToLoginOrDashboard(currentPath)) {
    if (isUserAuthenticated()) {
      // User is authenticated, show spinner while redirecting to dashboard
      return <LoadingSpinner />;
    }
    // User is not authenticated, redirect immediately to login
    redirectToLogin();
  }

  // Parse the current path to handle all routing logic
  const { routeParts, connectIndex } = parseRoute(currentPath);

  // If we have a connect route, handle it
  if (connectIndex !== -1 && connectIndex + 1 < routeParts.length) {
    const routeType = routeParts[connectIndex + 1];

    // Handle different connect routes
    switch (routeType) {
      case 'campaigns': {
        const campaignsIndex = connectIndex + 1;
        if (campaignsIndex + 1 < routeParts.length) {
          const campaignId = routeParts[campaignsIndex + 1];

          // Check if there's a sub-route after the campaign ID
          if (campaignsIndex + 2 < routeParts.length) {
            const subRoute = routeParts[campaignsIndex + 2];

            switch (subRoute) {
              case 'curation':
                return <CampaignPageCuration campaignId={campaignId} />;
              case 'collaborations':
                return <CampaignPageCollaborations campaignId={campaignId} />;
              case 'drafts':
                return <CampaignPageDrafts campaignId={campaignId} />;
              case 'summary':
                return <CampaignPageSummary campaignId={campaignId} />;
              case 'sentiment':
                return <CampaignPageSentiment campaignId={campaignId} />;
              case 'beat': {
                const beatId = routeParts[campaignsIndex + 3];
                if (campaignId && beatId) {
                  return <CampaignPageBeat campaignId={campaignId} beatId={beatId} />;
                }
                return handleError(t('error.campaignIdBeatIdRequired'), { campaignId, beatId });
              }
              default:
                return handleError(t('error.unknownCampaignRoute', { route: subRoute }), {
                  subRoute,
                  campaignId
                });
            }
          }
          // /connect/campaigns/<campaign_id> (campaign detail page)
          return <CampaignPageList campaignId={campaignId} />;
        }
        // /connect/campaigns (no campaign ID)
        return <Campaigns />;
      }
      case 'settings':
        return <Settings />;
      case 'team':
        return <Team />;
      case 'content-hub':
        return <ContentHub />;
      case 'plans':
        return <CampaignPlans />;
      case 'payouts':
        return <Payouts />;
      case 'sentiment':
        return <Sentiment />;
      case 'ambassadors':
        return <Ambassadors />;
      case 'collaborations': {
        const collaborationsIndex = connectIndex + 1;
        if (collaborationsIndex + 1 < routeParts.length) {
          const collaborationId = routeParts[collaborationsIndex + 1];

          // Check if there's a sub-route after the collaboration ID
          if (collaborationsIndex + 2 < routeParts.length) {
            const subRoute = routeParts[collaborationsIndex + 2];
            if (subRoute === 'invite') {
              // Handle /connect/collaborations/<id>/invite
              return <InviteView collaborationId={collaborationId} />;
            }

            // Handle new collaboration sub-routes that redirect to tab-based URLs
            if (subRoute === 'drafts') {
              return redirectCollaborationSubRoute(collaborationId, 'DRAFTS');
            }
            if (subRoute === 'publications') {
              return redirectCollaborationSubRoute(collaborationId, 'PUBLICATIONS');
            }
            if (subRoute === 'statistics') {
              return redirectCollaborationSubRoute(collaborationId, 'SCREENSHOTS1');
            }

            // Handle other sub-routes if needed
            return handleError(t('error.unknownCollaborationRoute', { route: subRoute }), {
              subRoute,
              collaborationId
            });
          }
          // /connect/collaborations/<collaboration_id> (collaboration detail page)
          // Extract beat and tab parameters from query string
          const urlParams = new URLSearchParams(window.location.search);
          const beatId = urlParams.get('beat');
          const tab = urlParams.get('tab');

          return (
            <Collaboration
              collaborationId={collaborationId}
              beatId={beatId || undefined}
              tab={tab || undefined}
            />
          );
        }
        // /connect/collaborations (list view)
        return <Collaborations />;
      }
      default:
        return handleError(t('error.unknownConnectRoute', { route: routeType }), {
          routeType,
          routeParts
        });
    }
  }

  // Handle influencer routes: /influencer/<id>
  if (routeParts.length === 2 && routeParts[0] === 'influencer') {
    const influencerId = routeParts[1];
    if (influencerId) {
      // Extract JWT token from query parameters if present
      const urlParams = new URLSearchParams(window.location.search);
      const jwtToken = urlParams.get('jwt');

      return <Influencer influencerId={influencerId} jwt={jwtToken || undefined} />;
    }
  }

  // Handle login route FIRST - before pendingNavigation check
  if (currentPath === '/login' || currentPath.match(/^\/[a-z]{2}\/login$/)) {
    return <Login />;
  }

  // Handle signup route FIRST - before pendingNavigation check
  if (currentPath === '/signup' || currentPath.match(/^\/[a-z]{2}\/signup$/)) {
    return <Signup />;
  }

  // Show loading spinner if there's a pending navigation
  if (pendingNavigation) {
    return <LoadingSpinner />;
  }

  // Handle verify-token route
  if (
    currentPath.match(/^\/verify-token\/[^/]+/) ||
    currentPath.match(/^\/[a-z]{2}\/verify-token\/[^/]+/)
  ) {
    // Extract JWT token from URL path
    const jwtMatch = currentPath.match(/\/verify-token\/([^/]+)/);
    const jwt = jwtMatch ? jwtMatch[1] : '';

    return <VerifyToken jwt={jwt} />;
  }

  // Handle verify-email route
  if (
    currentPath.match(/^\/verify-email\/[^/]+/) ||
    currentPath.match(/^\/[a-z]{2}\/verify-email\/[^/]+/)
  ) {
    // Redirect to /connect (same behavior as the original verify-email page)
    // Use safeNavigate to avoid hard navigation conflicts
    if (typeof window !== 'undefined') {
      safeNavigate(router, fixUrlForCloudFront('/connect'));
    }

    return <LoadingSpinner />;
  }

  // Regular home page logic (for /)
  return null;
}

export default Home;
